import React, { useEffect, useState } from 'react';
import {
  Toolbar,
  CssBaseline,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
} from '@mui/material';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import DashboardContent from '../components/DashboardContent';
import AddBooking from '../components/NewBooking';
import AddUser from '../components/AddUser';
import RemoveUser from '../components/RemoveUser';
import History from '../components/History';
import Scorecard from '../components/Scorecard';
import AgreementGeneratorPage from './AgreementGeneratorPage';
import ServicesComponent from '../components/Servicescomponent';
import Trash from '../components/Trash';
import Proformainvoice from '../Pages/InvoicePage';
import { apiUrl } from '../components/LoginSignup';
import { CreateProfile } from '../components/EmployeeProfileForm';
import CompanyProfile from '../components/CompanyProfile';
import DocumentsPage from '../components/DocumentsPage';
import NotificationBell from '../components/NotificationBell';
import DynamicHead from '../components/DynamicHead';
import TeamInbox from '../components/TeamInbox';
import ChatFAB from '../components/ChatFAB';
import ProjectionLeads from '../components/ProjectionLeads';
import ClientDocuments from '../components/ClientDocuments';
import { canAccessFeature } from '../utils/featureAccess';
import SecuritySettings from '../components/SecuritySettings';
import BookingApprovals from '../components/BookingApprovals';
import MeetingUpdates from '../components/MeetingUpdates';
import LeadsManagement from '../components/LeadsManagement';

const FeatureGuard = ({ userSession, feature, children }) => {
  if (canAccessFeature(userSession, feature)) return children;

  return (
    <Box
      sx={{
        minHeight: 300,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 3,
        border: '1px dashed',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        p: 3,
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ fontSize: 32, mb: 1 }}>🔒</Box>
        <Box sx={{ fontWeight: 700, color: 'text.primary' }}>Access restricted</Box>
        <Box sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
          Your role does not currently have permission for this feature.
        </Box>
      </Box>
    </Box>
  );
};

const InitialLoader = ({ theme }) => {
  const [progress, setProgress] = useState(0);
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const res = await fetch(`${apiUrl}/company/public`);
        const data = await res.json();
        if (res.ok && data.logo_url) setCompanyLogo(data.logo_url);
      } catch (e) {}
    };
    fetchBranding();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress >= 100) {
          clearInterval(timer);
          return 100;
        }
        const diff = Math.random() * 20;
        return Math.min(oldProgress + diff, 100);
      });
    }, 150);

    return () => {
      clearInterval(timer);
    };
  }, []);

  const isLight = theme.palette.mode === 'light';

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: isLight ? '#ffffff' : '#0a0a0a',
        color: isLight ? '#0f172a' : '#ffffff'
      }}
    >
      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        {companyLogo && (
          <img src={companyLogo} alt="Loading Logo" style={{ height: 60, objectFit: 'contain', marginBottom: 24 }} />
        )}
        <Box sx={{ width: 240, height: 4, bgcolor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden', mb: 1 }}>
          <Box sx={{ width: `${progress}%`, height: '100%', bgcolor: '#ff3b1f', transition: 'width 0.15s linear' }} />
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 600, color: isLight ? '#64748b' : '#9ca3af' }}>
          {Math.floor(progress)}% Loading...
        </Typography>
      </Box>
    </Box>
  );
};

const Dashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const theme = useTheme();
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const userSession = JSON.parse(localStorage.getItem('userSession')) || {};

  const [showInitialLoader, setShowInitialLoader] = useState(() => {
    return false;
  });

  useEffect(() => {
    if (showInitialLoader) {
      // simulate 1.5s loading time for 100% animation
      const timer = setTimeout(() => {
        setShowInitialLoader(false);
        sessionStorage.setItem('crm_initial_load', 'true');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [showInitialLoader]);

  useEffect(() => {
    const userSession = JSON.parse(localStorage.getItem('userSession')) || {};

    if (!userSession.name) {
      console.warn('No user name found in session data');
    }
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const mobilePageTitleMap = {
    '/dashboard': 'Dashboard',
    '/dashboard/new-booking': 'New Booking',
    '/dashboard/booking-approvals': 'Booking Approvals',
    '/dashboard/projection-leads': 'Projection Leads',
    '/dashboard/leads-management': 'Leads Management',
    '/dashboard/history': 'All Bookings',
    '/dashboard/adduser': 'Manage Users',
    '/dashboard/removeuser': 'Manage Users',
    '/dashboard/scorecard': 'Scorecard',
    '/dashboard/addservices': 'Manage Services',
    '/dashboard/trash': 'Trash',
    '/dashboard/Proformainvoice': 'Proforma Invoice',
    '/dashboard/Agreementsgenerator': 'Agreements Generator',
    '/dashboard/employee-profile': 'Employee Profile',
    '/dashboard/company-profile': 'Company Profile',
    '/dashboard/security': 'Security',
    '/dashboard/generated-documents': 'Generated Documents',
    '/dashboard/process-documents': 'File Activity',
    '/dashboard/meeting-updates': 'Meeting Updates',
    '/dashboard/timecard': 'HRM Portal',
    '/dashboard/communication': 'Communication',
  };

  const mobilePageTitle = mobilePageTitleMap[location.pathname] || 'Dashboard';

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: 'background.default',
      }}
    >
      {showInitialLoader && <InitialLoader theme={theme} />}
      <CssBaseline />
      <DynamicHead />

      <Sidebar isOpen={isSidebarOpen} onToggle={toggleSidebar} />

      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            height: 56,
            display: 'flex',
            alignItems: 'center',
            pl: 6,
            pr: 7,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(2,6,23,0.88)' : 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(14px)',
            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.12)' : 'rgba(15,23,42,0.06)'}`,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1 }}>
            {mobilePageTitle}
          </Typography>
        </Box>
      )}

      {/* Notification Bell — fixed top-right */}
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 10, sm: 12 },
          right: { xs: 12, sm: 20 },
          zIndex: 1300,
        }}
      >
        <NotificationBell />
      </Box>

      {/* Floating Chat Icon — fixed bottom-right */}
      <ChatFAB />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          marginLeft: isTabletOrBelow ? 0 : '250px',
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          p: { xs: 1.2, sm: 2, md: 2.5, lg: 3 },
          pt: { xs: 8, sm: 2, md: 2.5, lg: 3 },
        }}
      >
        {isTabletOrBelow && !isMobile && <Toolbar />}

        <Routes>
          <Route path="/" element={<FeatureGuard userSession={userSession} feature="dashboard_overview"><DashboardContent /></FeatureGuard>} />
          <Route path="new-booking" element={<FeatureGuard userSession={userSession} feature="new_booking"><AddBooking /></FeatureGuard>} />
          <Route path="booking-approvals" element={<FeatureGuard userSession={userSession} feature="booking_approvals"><BookingApprovals /></FeatureGuard>} />
          <Route path="projection-leads" element={<FeatureGuard userSession={userSession} feature="projection_leads"><ProjectionLeads /></FeatureGuard>} />
          <Route path="leads-management" element={<FeatureGuard userSession={userSession} feature="lead_management"><LeadsManagement /></FeatureGuard>} />
          <Route path="history" element={<FeatureGuard userSession={userSession} feature="all_bookings"><History /></FeatureGuard>} />
          <Route path="adduser" element={<FeatureGuard userSession={userSession} feature="manage_users"><AddUser /></FeatureGuard>} />
          <Route path="removeuser" element={<FeatureGuard userSession={userSession} feature="manage_users"><RemoveUser /></FeatureGuard>} />
          <Route path="scorecard" element={<FeatureGuard userSession={userSession} feature="scorecard"><Scorecard /></FeatureGuard>} />
          <Route path="addservices" element={<FeatureGuard userSession={userSession} feature="manage_services"><ServicesComponent /></FeatureGuard>} />
          <Route path="trash" element={<FeatureGuard userSession={userSession} feature="trash"><Trash /></FeatureGuard>} />
          <Route path="Proformainvoice" element={<FeatureGuard userSession={userSession} feature="proforma_invoice"><Proformainvoice /></FeatureGuard>} />
          <Route path="Agreementsgenerator" element={<FeatureGuard userSession={userSession} feature="agreements_generator"><AgreementGeneratorPage /></FeatureGuard>} />
          <Route path="employee-profile" element={<FeatureGuard userSession={userSession} feature="employee_profile"><CreateProfile apiUrl={apiUrl} userSession={userSession} /></FeatureGuard>} />
          <Route path="company-profile" element={<FeatureGuard userSession={userSession} feature="company_profile"><CompanyProfile /></FeatureGuard>} />
          <Route path="security" element={<FeatureGuard userSession={userSession} feature="security"><SecuritySettings /></FeatureGuard>} />
          <Route path="generated-documents" element={<FeatureGuard userSession={userSession} feature="generated_documents"><DocumentsPage /></FeatureGuard>} />
          <Route path="process-documents" element={<FeatureGuard userSession={userSession} feature="client_documents"><ClientDocuments /></FeatureGuard>} />
          <Route path="meeting-updates" element={<FeatureGuard userSession={userSession} feature="meeting_updates"><MeetingUpdates /></FeatureGuard>} />
          <Route path="timecard" element={<FeatureGuard userSession={userSession} feature="timecard"><Navigate to="/hrm" replace /></FeatureGuard>} />
          <Route path="communication" element={<FeatureGuard userSession={userSession} feature="communication"><TeamInbox /></FeatureGuard>} />
        </Routes>
      </Box>
    </Box>
  );
};

export default Dashboard;
